import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../../../utils/api';

const WorkDiaryPhotosModal = ({ isOpen, onClose, onInsert, selectedPeriod, selectedSite }) => {
  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && selectedPeriod.startDate && selectedPeriod.endDate) {
      fetchWorkDiaryPhotos();
    }
  }, [isOpen, selectedPeriod]);

  const fetchWorkDiaryPhotos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use dates directly in YYYY-MM-DD format (no conversion needed)
      const startDate = selectedPeriod.startDate;
      const endDate = selectedPeriod.endDate;
      
      const response = await axios.get(`${API}/admin/work-diary-photos`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(selectedSite && { site_id: selectedSite })
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setAvailablePhotos(response.data || []);
    } catch (error) {
      console.error('Error fetching work diary photos:', error);
      setError('Failed to load work diary photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const selectAll = () => {
    setSelectedPhotos(availablePhotos.map(photo => photo.id));
  };

  const deselectAll = () => {
    setSelectedPhotos([]);
  };

  const handleInsert = () => {
    const photosToInsert = availablePhotos.filter(photo => selectedPhotos.includes(photo.id));
    onInsert(photosToInsert);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">📷 Work Diary Photos</h2>
            <p className="text-sm text-gray-600 mt-1">
              Select photos from your work diary to include in the report
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {availablePhotos.length} photos available
              </span>
              <span className="text-sm text-blue-600">
                {selectedPhotos.length} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={selectAll}
                disabled={availablePhotos.length === 0}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                disabled={selectedPhotos.length === 0}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Deselect All
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading photos...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700">{error}</p>
              <button
                onClick={fetchWorkDiaryPhotos}
                className="mt-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && availablePhotos.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 font-medium">No work diary photos found</p>
              <p className="text-gray-500 text-sm mt-1">
                No photos were found for the selected period and site
              </p>
            </div>
          )}

          {!loading && !error && availablePhotos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {availablePhotos.map((photo) => (
                <div
                  key={photo.id}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    selectedPhotos.includes(photo.id)
                      ? 'border-blue-500 ring-2 ring-blue-200 transform scale-105'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                  onClick={() => togglePhotoSelection(photo.id)}
                >
                  <img
                    src={photo.gcs_url || photo.url}
                    alt={`Work diary photo ${photo.id}`}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity duration-200"></div>
                  
                  {selectedPhotos.includes(photo.id) && (
                    <div className="absolute top-2 right-2 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                      ✓
                    </div>
                  )}
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-white text-xs font-medium">
                      {new Date(photo.upload_date || photo.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <span className="text-sm text-gray-600">
            {selectedPhotos.length} photo(s) selected
          </span>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              disabled={selectedPhotos.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Insert Selected ({selectedPhotos.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkDiaryPhotosModal;